import { Box, Typography } from "@mui/material";
import type { DateTime } from "luxon";
import { type FC, useCallback, useDeferredValue, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { useAnalytics } from "../../../contexts/analytics/hooks";
import { useHospitalData } from "../../../contexts/HospitalDataProvider";
import { useWeekIndex } from "../../../contexts/WeekIndexProvider/hook";
import { useWeekViewContext } from "../../../contexts/WeekView/WeekViewContext";
import { useDepartmentCallInsForDay } from "../../../hooks/useCallIns";
import { BOTTOM_NAV, FONT_SIZE, INK, NEUTRAL, SURFACE } from "../../../theme/tokens";
import type { OpenShiftRequest } from "../../../utils/models";
import { usePageScheduleHolidays } from "../../team/Holidays/schedule/scheduleHolidaysContext";
import { SystemTabs } from "../../UIToolkit/Clickable/TabsAndChips/SystemTabs/SystemTabs";
import type { SystemTab } from "../../UIToolkit/Clickable/TabsAndChips/SystemTabs/utils";
import { EmptyState } from "../../UIToolkit/molecules/EmptyState";
import { MobileCallInsList } from "../mobile/CallInsList/MobileCallInsList";
import { GapsClaimsBanner } from "./GapsClaimsBanner";
import { GapsFilterSheet } from "./GapsFilterSheet";
import { GapsListScroller } from "./GapsListScroller";
import { GapsMetricsLine } from "./GapsMetricsLine";
import { GapsSearchRow } from "./GapsSearchRow";
import { GapsShiftGroup } from "./GapsShiftGroup";
import { GapsWeekStrip } from "./GapsWeekStrip";
import { GapsWhatsNewNotice } from "./GapsWhatsNewNotice";
import { countDistinctStaff, type GapsMissingEntry, type GapsShiftGroup as GapsShiftGroupModel, isPostedRequestInScope } from "./gapsDayModel";
import type { GapsScopedShiftGroup } from "./gapsGroupSheets";
import { isGapsHomeReset } from "./gapsHomeReset";
import { staffingDayKey } from "./gapsRequests";
import { GapsPostedList } from "./openShifts/GapsPostedList";
import { GapsOpenShiftsLoad, useGapsOpenShiftsLoad } from "./openShifts/useGapsOpenShiftsLoad";
import { GapsSheets } from "./sheets/GapsSheets";
import { useGapsCountsHidden } from "./useGapsCountsHidden";
import { useGapsHomeModel } from "./useGapsDayModel";
import { GapsAssignUnitReason, GapsSheetKind, openGapsSheet, useCloseGapsSheetOffDay, useCloseGapsSheetOnUnmount } from "./useGapsSheet";
import { useGapsVisibleWeek } from "./useGapsVisibleWeek";
import { useTrackSettledSearch } from "./useTrackSettledSearch";

type Props = {
	date: DateTime;
	/** Moves the shell's URL date without a full navigation. */
	onDateChange: (next: DateTime) => void;
};

enum GapsTab {
	Staff = "staff",
	Posted = "posted",
	CallIns = "call-ins",
}

/**
 * The gaps-first mobile home.
 *
 * The screen re-weighted around the one question a head nurse asks on a phone: how many staff am I
 * missing, and where. Gaps per job on top, shifts grouped with `filled/required` on the header, and
 * one row per missing person named by the location that is short.
 *
 * It computes NOTHING. Every number comes from `useWeeklyGapView`, `DayItem.dayStats` and `useHppdByDimension`,
 * which are what the desktop weekly gap view and the Day Schedule header already print — this screen only
 * reshapes them for a 390px viewport. That is what makes the two verifiable against each other.
 *
 * Week paging drives the SHARED `useWeekIndex()` rather than local state. `WeekViewProvider` builds
 * `weekViewData` from `hospitalData.weeks[weekIndex]`, so moving the shared index re-slices data the
 * app already holds — the seven numbers on the strip change and no request fires.
 */
export const MobileGapsStaffing: FC<Props> = ({ date, onDateChange }) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { track } = useAnalytics();
	const { chosenDepartment, hospitalData, isMedicalDepartment, getNurseFormattedName } = useHospitalData();
	const { weekIndex, setWeekIndex } = useWeekIndex();
	const { costlyShiftsByDay } = useWeekViewContext();

	const [activeTab, setActiveTab] = useState<GapsTab>(GapsTab.Staff);
	// The bar's Schedule tap lands here with a reset (`isGapsHomeReset`): back on the Staff tab, even when the home stayed
	// mounted. Adjusted during render, once per navigation, so no frame shows the old tab under today's day.
	const location = useLocation();
	const [seenLocationKey, setSeenLocationKey] = useState<string>(location.key);
	if (location.key !== seenLocationKey) {
		setSeenLocationKey(location.key);
		if (isGapsHomeReset(location.state)) setActiveTab(GapsTab.Staff);
	}
	const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});
	const [isMetricsOpen, setIsMetricsOpen] = useState<boolean>(false);
	const [isFilterOpen, setIsFilterOpen] = useState<boolean>(false);
	const [searchTerm, setSearchTerm] = useState<string>("");
	const deferredSearch = useDeferredValue(searchTerm);

	const departmentId = chosenDepartment.department_id;
	// The URL's day key — also the day every sheet opened here is bound to.
	const dateISO = staffingDayKey(date);

	// The URL owns the day; the shared index has to follow it, or `weekViewData` would describe a
	// different week from the one the strip is drawing. Only writes when they actually disagree.
	//
	// Looked up by CALENDAR date, as classic does. The URL day is a calendar day at midnight, and
	// `getWeekIndexByDateTime` first rolls an instant back to the shift day it belongs to — midnight
	// falls before the department's day start, so a tap on the week's first day resolved to the day
	// before it and landed on the previous week. The index is the VISIBLE week the desktop draws for that
	// day, which differs from its data week when weeks start after Sunday.
	const { weekIndexForDate: visibleWeekIndexOf, dayInWeek } = useGapsVisibleWeek();
	const weekIndexForDate = visibleWeekIndexOf(date);
	useEffect(() => {
		if (weekIndexForDate >= 0 && weekIndexForDate !== weekIndex) setWeekIndex(weekIndexForDate);
	}, [weekIndexForDate, weekIndex, setWeekIndex]);

	const home = useGapsHomeModel(date);
	const filterState = home.filterState;
	const { areCountsHidden } = useGapsCountsHidden();

	const weekDates = useMemo<DateTime[]>(() => home.weekDays.map((day) => day.date), [home.weekDays]);
	const { holidayOn } = usePageScheduleHolidays();

	const lastWeekIndex = Math.max(0, hospitalData.weeks.length - 1);

	// Pages by moving the URL day to the same column of the neighbouring visible week, and lets the effect above
	// move the shared index after it. Setting the index directly cannot work while the URL owns the day: the sync
	// effect would put it straight back on the week the URL still names. Still no fetch — the target day comes out
	// of `hospitalData.weeks`, which is already loaded. Before the day is in the loaded week there is no column to
	// keep, so the arrow waits rather than guessing one.
	const pageWeek = useCallback(
		(delta: number): void => {
			const next = Math.min(lastWeekIndex, Math.max(0, weekIndex + delta));
			if (next === weekIndex || home.dayIndex < 0) return;
			const target = dayInWeek(next, home.dayIndex);
			if (target === undefined) return;
			track("gaps_week_paged", { department_id: departmentId, direction: delta > 0 ? "next" : "previous" });
			onDateChange(target);
		},
		[weekIndex, lastWeekIndex, home.dayIndex, dayInWeek, onDateChange, track, departmentId],
	);

	// ── Requests and call-ins for the two other tabs, from the same sources classic uses ──
	// Narrowed by the saved scope as the Staff list is (user decision): a supervisor scoped to their carts
	// is asking about their carts' posts too. `postedScope` is the desktop matcher's own placement, so a
	// request is kept or dropped by the same job/window/unit it is drawn under on the weekly gap view. The day's
	// list itself is the model's (`postedRequestsForDay`), the same one the pick-up bonuses are summed over.
	const requests = useMemo<OpenShiftRequest[]>(
		() => home.dayPostedRequests.filter((request) => isPostedRequestInScope(request.id, home.postedScope)),
		[home.dayPostedRequests, home.postedScope],
	);
	const postedLoad = useGapsOpenShiftsLoad();

	const { callIns, isLoading: isLoadingCallIns, isError: isCallInsError } = useDepartmentCallInsForDay({ date, enabled: !isMedicalDepartment });

	// The same overtime signal the staffing grid marks its cells from, narrowed to the day on screen.
	const overtimeShiftIds = useMemo<ReadonlySet<string>>(() => {
		// DEFAULT: the map holds only days with a shift driving overtime, so a day absent from it has none — the
		// same reading `WeekViewProvider` gives it when it fills `costlyShiftsForDay`.
		const costly = costlyShiftsByDay.get(dateISO) ?? [];
		return new Set(costly.map((shift) => shift.id));
	}, [costlyShiftsByDay, dateISO]);

	// ── Search cuts across every group at once and opens what matches ──
	const query = deferredSearch.trim().toLowerCase();
	const visibleGroups = useMemo<GapsScopedShiftGroup[]>(() => {
		if (!query) return home.groups;
		return home.groups
			.map((group) => ({ ...group, staff: group.staff.filter((entry) => entry.slot.name.toLowerCase().includes(query)) }))
			.filter((group) => group.staff.length > 0);
	}, [home.groups, query]);

	// Call-ins match by the name their row prints (`getNurseFormattedName`), so a search can only ever find
	// what the list would have shown.
	const visibleCallIns = useMemo(
		() => (query ? callIns.filter((callIn) => getNurseFormattedName(callIn.nurseId).toLowerCase().includes(query)) : callIns),
		[callIns, query, getNurseFormattedName],
	);

	const staffCount = useMemo<number>(() => countDistinctStaff(home.groups), [home.groups]);

	// Counted from the same deferred term the lists are filtered by, and only once the searched tab's list has loaded.
	// Posted keeps the term but takes no search, so a term that settles there waits for a tab it searches.
	useTrackSettledSearch(deferredSearch, {
		departmentId,
		tab: activeTab,
		results: activeTab === GapsTab.CallIns ? visibleCallIns.length : countDistinctStaff(visibleGroups),
		isReady: activeTab === GapsTab.Staff ? home.isDayReady : activeTab === GapsTab.CallIns && !isLoadingCallIns && !isCallInsError,
	});

	const isGroupOpen = useCallback((key: string): boolean => (query ? true : (openGroups[key] ?? false)), [openGroups, query]);

	const toggleGroup = useCallback((key: string): void => setOpenGroups((prev) => ({ ...prev, [key]: !prev[key] })), []);

	// The sheets' intent outlives this page on purpose (a trigger elsewhere opens it, then navigates here); leaving
	// the page is what closes it, so a sheet never reappears on the next visit or in another department. Moving to
	// another day under an open sheet closes it too (the bar's Gaps slot, the phone's back button): a sheet acts on the
	// day it was opened on, never on the one the home moved to.
	useCloseGapsSheetOnUnmount();
	useCloseGapsSheetOffDay(dateISO);

	// ── Taps. Every one opens its sheet in place, over the day's gaps (PR 3 swapped each PR 2 destination for its
	// sheet without moving the tap target). ──
	const handlePostShift = useCallback(
		(group: GapsShiftGroupModel): void => {
			track("gaps_post_shift_opened", { department_id: departmentId, job: group.jobLabel, shift: group.timeLabel, source: "group" });
			openGapsSheet({ kind: GapsSheetKind.PostShift, dayISO: dateISO, groupKey: group.key });
		},
		[dateISO, departmentId, track],
	);

	// Pre-scoped to the row's unit, job and slot hours, and LINKED to the posted request standing against the hole when
	// there is one (P3-8) — the desktop opens that request for the same cell. The scope is captured by value: a missing
	// row's own key is positional, and the unit and hours the user tapped must not move under them.
	const handleAddToMissing = useCallback(
		(group: GapsShiftGroupModel, entry: GapsMissingEntry): void => {
			track("gaps_missing_row_clicked", {
				department_id: departmentId,
				job: group.jobLabel,
				location: entry.locationName,
				has_open_request: entry.hasOpenRequest,
			});
			openGapsSheet({
				kind: GapsSheetKind.AddStaff,
				dayISO: dateISO,
				scope: { groupKey: group.key, locationName: entry.locationName, startMin: entry.startMin, endMin: entry.endMin },
				linkedRequestId: entry.postedRequestId,
				// Scoped to the hole, which names its own group.
				groupKey: null,
			});
		},
		[dateISO, departmentId, track],
	);

	const handleOpenStaff = useCallback(
		(nurseInShiftId: string): void => {
			track("gaps_staff_row_opened", { department_id: departmentId, nurse_in_shift_id: nurseInShiftId });
			openGapsSheet({ kind: GapsSheetKind.StaffActions, dayISO: dateISO, shiftId: nurseInShiftId });
		},
		[dateISO, departmentId, track],
	);

	// Its own handler and event (P3-6): "Assign unit" goes straight to the unit picker, not through the actions sheet.
	const handleAssignUnit = useCallback(
		(nurseInShiftId: string): void => {
			track("gaps_staff_assign_unit_clicked", { department_id: departmentId, nurse_in_shift_id: nurseInShiftId });
			openGapsSheet({ kind: GapsSheetKind.AssignUnit, dayISO: dateISO, shiftId: nurseInShiftId, reason: GapsAssignUnitReason.StaffRow });
		},
		[dateISO, departmentId, track],
	);

	// The app's tab bar rather than hand-drawn pills: counts ride on its `badge`, the same count pill every
	// other `SystemTabs` screen uses.
	const tabs = useMemo<SystemTab<GapsTab>[]>(() => {
		const countBadge = (count: number) => ({ content: count, color: "primary" as const });
		return [
			// Both counts are read off the gap view, so they wait for it to settle rather than print a provisional number.
			{ value: GapsTab.Staff, label: t("gaps.tab.staff"), badge: home.isDayReady ? countBadge(staffCount) : undefined },
			// The Posted count also waits for THIS department's requests, as its list does: an unsettled or failed load is
			// not "0 posted", and until the new department's load lands the list still holds the previous department's.
			{
				value: GapsTab.Posted,
				label: t("gaps.tab.posted"),
				badge: home.isDayReady && postedLoad === GapsOpenShiftsLoad.Ready ? countBadge(requests.length) : undefined,
			},
			...(isMedicalDepartment ? [] : [{ value: GapsTab.CallIns, label: t("gaps.tab.callIns"), badge: countBadge(callIns.length) }]),
		];
	}, [t, staffCount, requests.length, callIns.length, isMedicalDepartment, home.isDayReady, postedLoad]);

	const handleTabChange = useCallback(
		(next: GapsTab): void => {
			track("gaps_tab_changed", { department_id: departmentId, tab: next });
			setActiveTab(next);
		},
		[track, departmentId],
	);

	return (
		// `data-ready` says the numbers on screen are final (saved settings and the day both loaded), so a test can
		// wait for it rather than read a strip or a count that is still deliberately blank.
		<Box data-testid="gaps-home-content" data-ready={home.isDayReady} sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0, width: "100%" }}>
			{/* Pinned chrome: the strip, the day's gap line, the urgent band and search. It sits OUTSIDE the list's
			    scroller, as the artifact's `.pinned` does, so it never scrolls and a drag across it can never start a
			    pull-to-refresh. */}
			<Box data-testid="gaps-home-pinned" sx={{ flex: "none", position: "relative", zIndex: 4, bgcolor: SURFACE, boxShadow: `0 1px 0 ${NEUTRAL[200]}` }}>
				<GapsWeekStrip
					days={weekDates}
					// Blank but height-kept both while the grouping is not final and while More → Hide gaps is on.
					shortfalls={home.isWeekReady && !areCountsHidden ? home.weekShortfalls : undefined}
					selectedDate={date}
					onSelectDate={onDateChange}
					onPreviousWeek={() => pageWeek(-1)}
					onNextWeek={() => pageWeek(1)}
					canGoBack={weekIndex > 0}
					canGoForward={weekIndex < lastWeekIndex}
					holidayForDay={holidayOn}
				/>
				<GapsMetricsLine
					jobTotals={home.isDayReady ? home.jobTotals : undefined}
					dayStats={home.dayStats}
					dayHppd={home.dayHppd}
					date={date}
					isExpanded={isMetricsOpen}
					onToggle={() => setIsMetricsOpen((wasOpen) => !wasOpen)}
					isNarrowed={filterState.isNarrowed}
					isActual={home.isDayReady && home.isDayActual}
				/>
				<GapsClaimsBanner onOpen={() => navigate(`/${departmentId}/requests`)} />
				<GapsSearchRow
					searchTerm={searchTerm}
					onSearchChange={setSearchTerm}
					filter={filterState.filter}
					isNarrowed={filterState.isNarrowed}
					onOpenFilter={() => setIsFilterOpen(true)}
					// Call-ins are the day's record of who called in, not a scoped view: the filter does not
					// apply to them, so on that tab it is shown but cannot be opened (user decision).
					isFilterDisabled={activeTab === GapsTab.CallIns}
					// Posted is a list of open shifts, not people — there is no name to search (user decision).
					isSearchDisabled={activeTab === GapsTab.Posted}
				/>
			</Box>

			<GapsListScroller idPrefix="gaps-home">
				{/* First row of the list, not pinned chrome — the artifact scrolls its tabs away with the list,
				    so the pinned block is only what every tab needs: the week, the gaps and search. */}
				<SystemTabs<GapsTab>
					tabs={tabs}
					currentTab={activeTab}
					onTabChange={handleTabChange}
					dataTestId="gaps-tab"
					sx={{ px: 1, bgcolor: SURFACE, borderBottom: `1px solid ${NEUTRAL[150]}` }}
				/>
				{/* Nothing, rather than an empty state, until the day is ready: a day still loading is not "no shifts". */}
				{activeTab === GapsTab.Staff && home.isDayReady && (
					// `data-searching`: the deferred search has applied a term, which a test waits on before reading rows.
					<Box data-testid="gaps-groups" data-searching={query === "" ? "false" : "true"}>
						{visibleGroups.map((group) => (
							<GapsShiftGroup
								key={group.key}
								group={group}
								isOpen={isGroupOpen(group.key)}
								onToggle={() => toggleGroup(group.key)}
								onPostShift={handlePostShift}
								onOpenStaff={handleOpenStaff}
								onAssignLocation={handleAssignUnit}
								onAddToMissing={handleAddToMissing}
								overtimeShiftIds={overtimeShiftIds}
								isDayPast={home.isDayPast}
								showsMissingRows={!home.isDayPast && query === ""}
							/>
						))}
						{visibleGroups.length === 0 && (
							<EmptyState
								dataTestId="gaps-groups-empty"
								title={
									query
										? t("gaps.empty.noMatches", { query: deferredSearch.trim() })
										: filterState.isNarrowed
											? t("gaps.empty.noShifts")
											: t("gaps.empty.noShiftsOnDay")
								}
								hint={!query && filterState.isNarrowed ? t("gaps.empty.noShiftsHint") : undefined}
								sx={{ py: 5 }}
							/>
						)}
					</Box>
				)}
				{activeTab === GapsTab.Posted && home.isDayReady && (
					<Box data-testid="gaps-posted-list" sx={{ px: 1.5, py: 1.5 }}>
						<GapsPostedList requests={requests} />
					</Box>
				)}
				{activeTab === GapsTab.CallIns && (
					<Box data-testid="gaps-call-ins-list" sx={{ px: 2, py: 1.5 }}>
						{query !== "" && callIns.length > 0 && visibleCallIns.length === 0 ? (
							<EmptyState dataTestId="gaps-call-ins-no-matches" title={t("gaps.empty.noMatches", { query: deferredSearch.trim() })} sx={{ py: 5 }} />
						) : (
							<MobileCallInsList callIns={visibleCallIns} isLoading={isLoadingCallIns} isError={isCallInsError} />
						)}
					</Box>
				)}
			</GapsListScroller>

			<GapsFilterSheet
				open={isFilterOpen}
				onClose={() => setIsFilterOpen(false)}
				available={home.available}
				filter={filterState.filter}
				onChange={filterState.setFilter}
				onClear={filterState.clearFilter}
				isSaved={filterState.isSaved}
				onSavedChange={filterState.setIsSaved}
				bottomOffset={BOTTOM_NAV.TOTAL_HEIGHT_CSS}
			/>
			<GapsSheets home={home} date={date} onMoveDay={onDateChange} />
			<GapsWhatsNewNotice departmentId={departmentId} />
		</Box>
	);
};
